import React from 'react';
import { CreditCard, FileText, DollarSign, Calendar } from 'lucide-react';
import MainTable from '../../../../../components/mainTable';
import { TableColumnsItem } from '../../../../../interfaces/components/mainTable';

interface BillingRecord {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue';
  insuranceClaim: string;
  cptCode: string;
  icdCode: string;
}

const mockBillingRecords: BillingRecord[] = [
  {
    id: '1',
    date: '2024-03-15',
    description: 'Annual Physical Examination',
    amount: 250.00,
    status: 'paid',
    insuranceClaim: 'CLM123456',
    cptCode: '99396',
    icdCode: 'Z00.00'
  },
  {
    id: '2',
    date: '2024-03-10',
    description: 'Blood Work - Complete Panel',
    amount: 180.00,
    status: 'pending',
    insuranceClaim: 'CLM123457',
    cptCode: '80053',
    icdCode: 'Z01.818'
  },
  {
    id: '3',
    date: '2024-02-28',
    description: 'Follow-up Consultation',
    amount: 120.00,
    status: 'paid',
    insuranceClaim: 'CLM123458',
    cptCode: '99213',
    icdCode: 'Z09'
  },
  {
    id: '4',
    date: '2024-02-15',
    description: 'X-Ray - Chest',
    amount: 150.00,
    status: 'overdue',
    insuranceClaim: 'CLM123459',
    cptCode: '71045',
    icdCode: 'R91.8'
  }
];

export const BillingTab: React.FC = () => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Paid
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case 'overdue':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Overdue
          </span>
        );
      default:
        return null;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const columns: TableColumnsItem[] = [
    {
      key: 'date',
      name: 'Date',
      type: 'text',
    },
    {
      key: 'description',
      name: 'Description',
      type: 'text',
    },
    {
      key: 'amount',
      name: 'Amount',
      type: 'custom',
      customValue: (row) => formatCurrency(row.amount),
    },
    {
      key: 'status',
      name: 'Status',
      type: 'custom',
      customValue: (row) => getStatusBadge(row.status),
    },
    {
      key: 'insuranceClaim',
      name: 'Claim #',
      type: 'text',
    },
    {
      key: 'cptCode',
      name: 'CPT Code',
      type: 'text',
    },
    {
      key: 'icdCode',
      name: 'ICD Code',
      type: 'text',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium dark:text-white">Billing & Coding</h2>
        <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#2823F5] dark:bg-[#4F4DFF] hover:bg-[#1f1bb8] dark:hover:bg-[#3F3DFF]">
          <CreditCard className="w-4 h-4 mr-2" />
          Add Billing Record
        </button>
      </div>

      <MainTable
        data={mockBillingRecords}
        columns={columns}
        showSearch={true}
        showAddButton={false}
      />
    </div>
  );
}; 